// ExportMp3Modal — pick a range of the FlowFrame and download it as MP3.
//
// Modes:
//   "all"       Render every measure in `measures`.
//   "selection" Render measures from `startMeasure` to `endMeasure` (1-indexed,
//               inclusive).
//
// The parent owns the actual rendering. This modal just collects inputs and
// drives the export by calling `onExport({ startMeasure, endMeasure, onProgress })`.
// It expects that to return a Promise resolving when the MP3 has downloaded
// (or rejecting on error).

import React, { useState } from 'react';

export default function ExportMp3Modal({
  measures,
  isPlaying,
  onClose,
  onExport,
}) {
  const totalMeasures = measures?.length || 0;

  const [mode, setMode] = useState('all');
  const [startMeasure, setStartMeasure] = useState(1);
  const [endMeasure, setEndMeasure] = useState(totalMeasures);
  const [phase, setPhase] = useState('idle'); // idle | recording | encoding | done | error
  const [progress, setProgress] = useState(0); // 0..1 within recording or encoding
  const [errorMsg, setErrorMsg] = useState('');

  const isBusy = phase === 'recording' || phase === 'encoding';

  const handleExport = async () => {
    const range = mode === 'all'
      ? { startMeasure: 1, endMeasure: totalMeasures }
      : { startMeasure: clampMeasure(startMeasure, totalMeasures), endMeasure: clampMeasure(endMeasure, totalMeasures) };

    if (range.endMeasure < range.startMeasure) {
      setErrorMsg('End measure must be on or after the start measure.');
      return;
    }

    setErrorMsg('');
    setProgress(0);
    setPhase('recording');

    try {
      await onExport({
        ...range,
        onProgress: (next) => {
          setPhase(next.phase || 'recording');
          if (typeof next.progress === 'number') setProgress(next.progress);
        },
      });
      setPhase('done');
    } catch (err) {
      console.error('[ExportMp3Modal] export failed:', err);
      setErrorMsg(err?.message || 'Export failed.');
      setPhase('error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Export to MP3</h2>
          <button
            onClick={onClose}
            disabled={isBusy}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-30"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {isPlaying && phase === 'idle' && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-900">
            Stop playback before exporting.
          </div>
        )}

        <fieldset disabled={isBusy} className="space-y-4">
          <div>
            <label className="block font-medium text-sm mb-2">Range</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="export-range"
                  value="all"
                  checked={mode === 'all'}
                  onChange={() => setMode('all')}
                />
                <span>Entire FlowFrame ({totalMeasures} {totalMeasures === 1 ? 'measure' : 'measures'})</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="export-range"
                  value="selection"
                  checked={mode === 'selection'}
                  onChange={() => setMode('selection')}
                />
                <span>Selection</span>
              </label>
            </div>
          </div>

          {mode === 'selection' && (
            <div className="flex items-center gap-3 pl-6">
              <label className="text-sm">
                From
                <input
                  type="number"
                  min={1}
                  max={totalMeasures}
                  value={startMeasure}
                  onChange={(e) => setStartMeasure(parseInt(e.target.value, 10) || 1)}
                  className="ml-2 w-16 border rounded px-2 py-1"
                />
              </label>
              <label className="text-sm">
                to
                <input
                  type="number"
                  min={1}
                  max={totalMeasures}
                  value={endMeasure}
                  onChange={(e) => setEndMeasure(parseInt(e.target.value, 10) || 1)}
                  className="ml-2 w-16 border rounded px-2 py-1"
                />
              </label>
            </div>
          )}
        </fieldset>

        {phase !== 'idle' && (
          <div className="mt-5">
            <div className="text-sm mb-1">{phaseLabel(phase)}</div>
            <div className="w-full h-2 bg-gray-200 rounded overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-150"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
          </div>
        )}

        {errorMsg && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-900">
            {errorMsg}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isBusy}
            className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            {phase === 'done' ? 'Close' : 'Cancel'}
          </button>
          {phase !== 'done' && (
            <button
              onClick={handleExport}
              disabled={isBusy || isPlaying || totalMeasures === 0}
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {phase === 'error' ? 'Retry' : 'Export'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function clampMeasure(n, max) {
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.min(max, Math.round(n)));
}

function phaseLabel(phase) {
  switch (phase) {
    case 'recording': return 'Recording…';
    case 'encoding': return 'Encoding MP3…';
    case 'done': return 'Done — file downloaded.';
    case 'error': return 'Failed.';
    default: return '';
  }
}
